import React, { useState, useEffect } from 'react';
import { FaChartBar, FaListOl, FaCalendarAlt, FaUsers, FaTrophy, FaUser, FaMedal, FaBalanceScale } from 'react-icons/fa';
import './statistics.css';
import { useTheme } from '../../context/ThemeContext';
import DatePickerCustom from '../../components/DatePickerCustom';
import { formatDateForInput } from '../../utils/formatters';

// Import các component con
import TeamStatistics from './TeamStatistics';
import TournamentStatistics from './TournamentStatistics';
import TeamUserRanking from './TeamUserRanking';
import TournamentUserRanking from './TournamentUserRanking';
import TournamentTeamRanking from './TournamentTeamRanking';
import PersonalAchievements from './PersonalAchievements';
import MaintenanceRankings from '../../components/Rankings/MaintenanceRankings';

// Import service liên quan
import { getTournaments } from '../../services/activitiesService';
import { getTournamentTeams } from '../../services/teamsService';
import { getUserId } from '../../services/authService';

// Enum cho các tab chính
enum MainTab {
  STATISTICS = 'statistics',
  RANKING = 'ranking',
  ACHIEVEMENTS = 'achievements'
}

// Enum cho các tab thống kê
enum StatisticsTab {
  TEAM = 'team',
  TOURNAMENT = 'tournament'
}

// Enum cho các tab xếp hạng
enum RankingTab {
  TEAM_USERS = 'team_users',
  TOURNAMENT_USERS = 'tournament_users',
  TOURNAMENT_TEAMS = 'tournament_teams',
  MAINTENANCE_RATIO = 'maintenance_ratio'
}

const Statistics: React.FC = () => {
  const { theme } = useTheme();
  
  // State cho tab hiện tại
  const [activeMainTab, setActiveMainTab] = useState<MainTab>(MainTab.STATISTICS);
  const [activeStatisticsTab, setActiveStatisticsTab] = useState<StatisticsTab>(StatisticsTab.TEAM);
  const [activeRankingTab, setActiveRankingTab] = useState<RankingTab>(RankingTab.MAINTENANCE_RATIO);
  const [maintenanceDisplayType, setMaintenanceDisplayType] = useState<'users' | 'teams'>('users');
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  
  // State cho các giá trị bộ lọc
  const [selectedTeamId, setSelectedTeamId] = useState<number>(0);
  const [selectedTournamentId, setSelectedTournamentId] = useState<number>(0);
  
  // State cho dữ liệu
  const [tournaments, setTournaments] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  
  // Lấy ngày hiện tại và ngày cách đây 1 năm làm mặc định
  const today = new Date();
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(today.getFullYear() - 1);
  
  const [startDate, setStartDate] = useState<string>(
    `${oneYearAgo.getFullYear()}-${String(oneYearAgo.getMonth() + 1).padStart(2, '0')}-${String(oneYearAgo.getDate()).padStart(2, '0')}T00:00:00`
  );
  const [endDate, setEndDate] = useState<string>(
    `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}T23:59:59`
  );
  
  // Lấy ID người dùng hiện tại khi component mount
  useEffect(() => {
    const userId = getUserId();
    setCurrentUserId(userId);
  }, []);
  
  // Lấy danh sách giải đấu khi component mount
  useEffect(() => {
    const fetchTournaments = async () => {
      try {
        const data = await getTournaments();
        if (data && data.length > 0) {
          setTournaments(data);
          // Tự động chọn giải đấu đầu tiên để trải nghiệm người dùng tốt hơn
          setSelectedTournamentId(data[0].id);
          
          // Tự động cập nhật khoảng thời gian theo thời gian của giải đấu đầu tiên
          const firstTournament = data[0];
          if (firstTournament) {
            // Xử lý trường hợp startDate và endDate có thể là array hoặc string
            let tournamentStartDate = '';
            let tournamentEndDate = '';
            
            if (Array.isArray(firstTournament.startDate)) {
              // Nếu là mảng [year, month, day, ...], chuyển thành chuỗi YYYY-MM-DD
              const [year, month, day] = firstTournament.startDate;
              tournamentStartDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}T00:00:00`;
            } else {
              // Nếu là chuỗi, đảm bảo định dạng đúng với T
              tournamentStartDate = firstTournament.startDate.includes('T') 
                ? firstTournament.startDate 
                : `${firstTournament.startDate}T00:00:00`;
            }
            
            if (Array.isArray(firstTournament.endDate)) {
              const [year, month, day] = firstTournament.endDate;
              tournamentEndDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}T23:59:59`;
            } else {
              tournamentEndDate = firstTournament.endDate.includes('T') 
                ? firstTournament.endDate 
                : `${firstTournament.endDate}T23:59:59`;
            }
            
            setStartDate(tournamentStartDate);
            setEndDate(tournamentEndDate);
          }
        }
      } catch (error) {
        console.error('Lỗi khi lấy danh sách giải đấu:', error);
      }
    };
    
    fetchTournaments();
  }, []);
  
  // Lấy danh sách đội khi chọn giải đấu
  useEffect(() => {
    const fetchTeams = async () => {
      if (selectedTournamentId) {
        try {
          const data = await getTournamentTeams(selectedTournamentId);
          if (data && data.length > 0) {
            setTeams(data);
            // Không tự động chọn đội đầu tiên để tránh gọi API thêm lần nữa
            // setSelectedTeamId(data[0].id);
          } else {
            setTeams([]);
            setSelectedTeamId(0);
          }
        } catch (error) {
          console.error('Lỗi khi lấy danh sách đội:', error);
          setTeams([]);
          setSelectedTeamId(0);
        }
      }
    };
    
    fetchTeams();
  }, [selectedTournamentId]);
  
  // Handler cho các tab chính
  const handleMainTabChange = (tab: MainTab) => {
    setActiveMainTab(tab);
  };
  
  // Handler cho các tab thống kê
  const handleStatisticsTabChange = (tab: StatisticsTab) => {
    setActiveStatisticsTab(tab);
    // Reset selectedTeamId nếu chuyển sang tab TOURNAMENT
    if (tab === StatisticsTab.TOURNAMENT) {
      setSelectedTeamId(0);
    }
  };
  
  // Handler cho các tab xếp hạng
  const handleRankingTabChange = (tab: RankingTab) => {
    setActiveRankingTab(tab);
    // Reset selectedTeamId nếu chuyển sang tab TOURNAMENT_USERS hoặc TOURNAMENT_TEAMS
    if (tab === RankingTab.TOURNAMENT_USERS || tab === RankingTab.TOURNAMENT_TEAMS) {
      setSelectedTeamId(0);
    }
  };
  
  // Handler cho thay đổi giải đấu
  const handleTournamentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const tournamentId = Number(e.target.value);
    setSelectedTournamentId(tournamentId);
    
    // Tự động cập nhật ngày bắt đầu và kết thúc dựa trên giải đấu được chọn
    if (tournamentId > 0) {
      const selectedTournament = tournaments.find(t => t.id === tournamentId);
      if (selectedTournament) {
        // Xử lý trường hợp startDate và endDate có thể là array hoặc string
        let tournamentStartDate = '';
        let tournamentEndDate = '';
        
        if (Array.isArray(selectedTournament.startDate)) {
          // Nếu là mảng [year, month, day, ...], chuyển thành chuỗi YYYY-MM-DD
          const [year, month, day] = selectedTournament.startDate;
          tournamentStartDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}T00:00:00`;
        } else {
          // Nếu là chuỗi, đảm bảo định dạng đúng với T
          tournamentStartDate = selectedTournament.startDate.includes('T') 
            ? selectedTournament.startDate 
            : `${selectedTournament.startDate}T00:00:00`;
        }
        
        if (Array.isArray(selectedTournament.endDate)) {
          const [year, month, day] = selectedTournament.endDate;
          tournamentEndDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}T23:59:59`;
        } else {
          tournamentEndDate = selectedTournament.endDate.includes('T') 
            ? selectedTournament.endDate 
            : `${selectedTournament.endDate}T23:59:59`;
        }
        
        // Luôn cập nhật ngày bắt đầu và kết thúc dựa trên giải đấu đã chọn
        setStartDate(tournamentStartDate);
        setEndDate(tournamentEndDate);
      }
    }
  };
  
  // Handler cho thay đổi đội
  const handleTeamChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newTeamId = Number(e.target.value);
    console.log(`Thay đổi đội từ ID: ${selectedTeamId} sang ID: ${newTeamId}`);
    setSelectedTeamId(newTeamId);
  };
  
  // Handler cho thay đổi ngày bắt đầu
  const handleStartDateChange = (date: Date | null) => {
    const dateStr = date ? date.toISOString().split('T')[0] : '';
    setStartDate(dateStr.includes('T') ? dateStr : `${dateStr}T00:00:00`);
  };
  
  // Handler cho thay đổi ngày kết thúc
  const handleEndDateChange = (date: Date | null) => {
    const dateStr = date ? date.toISOString().split('T')[0] : '';
    setEndDate(dateStr.includes('T') ? dateStr : `${dateStr}T23:59:59`);
  };
  
  // Handler cho thay đổi loại hiển thị tỷ lệ duy trì
  const handleMaintenanceDisplayTypeChange = (type: 'users' | 'teams') => {
    console.log(`Chuyển đổi chế độ hiển thị tỷ lệ duy trì: ${type}`);
    setMaintenanceDisplayType(type);
  };
  
  // Render bộ lọc chung cho cả thống kê và xếp hạng
  const renderFilters = () => {
    // Kiểm tra xem có giải đấu được chọn hay không
    const isTournamentSelected = selectedTournamentId > 0;
    const selectedTournament = tournaments.find(t => t.id === selectedTournamentId);
    
    return (
      <div className="statistics-filters">
        {(activeMainTab === MainTab.STATISTICS && activeStatisticsTab === StatisticsTab.TEAM) || 
         (activeMainTab === MainTab.RANKING && activeRankingTab === RankingTab.TEAM_USERS) ? (
          <>
            <div className="filter-item">
              <label htmlFor="tournament-select">Giải đấu</label>
              <select 
                id="tournament-select" 
                value={selectedTournamentId} 
                onChange={handleTournamentChange}
                className="filter-select"
              >
                <option value="0">-- Chọn giải đấu --</option>
                {tournaments.map(tournament => (
                  <option key={tournament.id} value={tournament.id}>
                    {tournament.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="filter-item">
              <label htmlFor="team-select">Đội</label>
              <select 
                id="team-select" 
                value={selectedTeamId} 
                onChange={handleTeamChange}
                className="filter-select"
                disabled={!selectedTournamentId || teams.length === 0}
              >
                <option value="0">-- Chọn đội --</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        ) : (activeMainTab === MainTab.STATISTICS && activeStatisticsTab === StatisticsTab.TOURNAMENT) || 
             (activeMainTab === MainTab.RANKING && (activeRankingTab === RankingTab.TOURNAMENT_USERS || 
              activeRankingTab === RankingTab.TOURNAMENT_TEAMS || activeRankingTab === RankingTab.MAINTENANCE_RATIO)) ? (
          <div className="filter-item">
            <label htmlFor="tournament-select">Giải đấu</label>
            <select 
              id="tournament-select" 
              value={selectedTournamentId} 
              onChange={handleTournamentChange}
              className="filter-select"
            >
              <option value="0">-- Chọn giải đấu --</option>
              {tournaments.map(tournament => (
                <option key={tournament.id} value={tournament.id}>
                  {tournament.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        
        {(activeMainTab === MainTab.STATISTICS || 
          (activeMainTab === MainTab.RANKING && activeRankingTab !== RankingTab.MAINTENANCE_RATIO) ||
          activeMainTab === MainTab.ACHIEVEMENTS) && (
          <>
        <div className="filter-item">
          <label>Từ ngày</label>
          <DatePickerCustom
            label=""
            value={startDate.split('T')[0]}
            onChange={handleStartDateChange}
            className="date-picker"
          />
        </div>
        
        <div className="filter-item">
          <label>Đến ngày</label>
          <DatePickerCustom
            label=""
            value={endDate.split('T')[0]}
            onChange={handleEndDateChange}
            className="date-picker"
          />
        </div>
          </>
        )}
      </div>
    );
  };
  
  // Render nội dung của tab thống kê
  const renderStatisticsContent = () => {
    // Hiển thị bộ lọc trước
    const filters = renderFilters();
    
    // Nếu chưa chọn giải đấu, hiển thị thông báo
    if (!selectedTournamentId) {
      return (
        <>
          {filters}
          <div className="empty-state">
            <FaTrophy className="empty-state-icon" />
            <div className="empty-state-text">Vui lòng chọn giải đấu để xem thống kê</div>
          </div>
        </>
      );
    }
    
    if (activeStatisticsTab === StatisticsTab.TEAM) {
      // Nếu không có đội được chọn, hiển thị thông báo
      if (!selectedTeamId || selectedTeamId <= 0) {
        return (
          <>
            {filters}
            <div className="empty-state">
              <FaUsers className="empty-state-icon" />
              <div className="empty-state-text">Vui lòng chọn đội để xem thống kê</div>
            </div>
          </>
        );
      }
      
      console.log(`Hiển thị thống kê cho đội ID: ${selectedTeamId}`);
      return (
        <>
          {filters}
          <TeamStatistics 
            key={`team-stats-${selectedTeamId}-${startDate}-${endDate}`}
            teamId={selectedTeamId} 
            startDateLocal={startDate} 
            endDate={endDate} 
          />
        </>
      );
    } else if (activeStatisticsTab === StatisticsTab.TOURNAMENT) {
      return (
        <>
          {filters}
          <TournamentStatistics 
            key={`tournament-stats-${selectedTournamentId}-${startDate}-${endDate}`}
            tournamentId={selectedTournamentId} 
            startDateLocal={startDate} 
            endDate={endDate} 
          />
        </>
      );
    }
    
    return null;
  };
  
  // Render nội dung của tab xếp hạng
  const renderRankingContent = () => {
    // Hiển thị bộ lọc trước
    const filters = renderFilters();
    
    // Nếu chưa chọn giải đấu, hiển thị thông báo
    if (!selectedTournamentId) {
      return (
        <>
          {filters}
          <div className="empty-state">
            <FaTrophy className="empty-state-icon" />
            <div className="empty-state-text">Vui lòng chọn giải đấu để xem xếp hạng</div>
          </div>
        </>
      );
    }
    
    if (activeRankingTab === RankingTab.TEAM_USERS) {
      // Nếu không có đội được chọn, hiển thị thông báo
      if (!selectedTeamId || selectedTeamId <= 0) {
        return (
          <>
            {filters}
            <div className="empty-state">
              <FaUsers className="empty-state-icon" />
              <div className="empty-state-text">Vui lòng chọn đội để xem xếp hạng thành viên</div>
            </div>
          </>
        );
      }
      
      return (
        <>
          {filters}
          <TeamUserRanking 
            key={`team-user-ranking-${selectedTeamId}-${startDate}-${endDate}`}
            teamId={selectedTeamId} 
            startDateLocal={startDate} 
            endDate={endDate} 
          />
        </>
      );
    } else if (activeRankingTab === RankingTab.TOURNAMENT_USERS) {
      return (
        <>
          {filters}
          {selectedTournamentId > 0 && (
            <TournamentUserRanking 
              key={`tournament-user-ranking-${selectedTournamentId}-${startDate}-${endDate}`}
              tournamentId={selectedTournamentId} 
              startDateLocal={startDate} 
              endDate={endDate} 
            />
          )}
        </>
      );
    } else if (activeRankingTab === RankingTab.TOURNAMENT_TEAMS) {
      return (
        <>
          {filters}
          {selectedTournamentId > 0 && (
            <TournamentTeamRanking 
              key={`tournament-team-ranking-${selectedTournamentId}-${startDate}-${endDate}`}
              tournamentId={selectedTournamentId} 
              startDateLocal={startDate} 
              endDate={endDate} 
            />
          )}
        </>
      );
    } else if (activeRankingTab === RankingTab.MAINTENANCE_RATIO) {
      return (
        <>
          {filters}
          
          {selectedTournamentId > 0 && (
            <>
              <div className="display-type-toggle" style={{ marginBottom: '20px' }}>
                <button 
                  className={`toggle-btn ${maintenanceDisplayType === 'users' ? 'active' : ''}`}
                  onClick={() => handleMaintenanceDisplayTypeChange('users')}
                >
                  <FaUser /> Xếp hạng cá nhân
                </button>
                <button 
                  className={`toggle-btn ${maintenanceDisplayType === 'teams' ? 'active' : ''}`}
                  onClick={() => handleMaintenanceDisplayTypeChange('teams')}
                >
                  <FaUsers /> Xếp hạng đội
                </button>
              </div>
              
              <MaintenanceRankings
                tournamentId={selectedTournamentId}
                userId={currentUserId || undefined}
                displayType={maintenanceDisplayType}
              />
            </>
          )}
        </>
      );
    }
    
    return null;
  };
  
  // Render nội dung của tab thành tích
  const renderAchievementsContent = () => {
    // Hiển thị bộ lọc trước
    const filters = renderFilters();
    
    // Nếu chưa chọn giải đấu, hiển thị thông báo
    if (!selectedTournamentId) {
      return (
        <>
          {filters}
          <div className="empty-state">
            <FaTrophy className="empty-state-icon" />
            <div className="empty-state-text">Vui lòng chọn giải đấu để xem thành tích</div>
          </div>
        </>
      );
    }
    
    return (
      <>
        {filters}
        
        <PersonalAchievements 
          startDateLocal={startDate} 
          endDate={endDate} 
        />
      </>
    );
  };
  
  return (
    <div className={`statistics-container ${theme}`}>
      <div className="statistics-header">
        <h1>Thống kê và bảng xếp hạng</h1>
        <p className="statistics-subtitle">Xem chi tiết thống kê, bảng xếp hạng và thành tích của người dùng, đội và giải đấu</p>
      </div>
      
      {/* Tab chính */}
      <div className="statistics-tabs main-tabs">
        <button 
          className={`statistics-tab ${activeMainTab === MainTab.STATISTICS ? 'active' : ''}`}
          onClick={() => handleMainTabChange(MainTab.STATISTICS)}
        >
          <FaChartBar /> Thống kê
        </button>
        <button 
          className={`statistics-tab ${activeMainTab === MainTab.RANKING ? 'active' : ''}`}
          onClick={() => handleMainTabChange(MainTab.RANKING)}
        >
          <FaListOl /> Xếp hạng
        </button>
        <button 
          className={`statistics-tab ${activeMainTab === MainTab.ACHIEVEMENTS ? 'active' : ''}`}
          onClick={() => handleMainTabChange(MainTab.ACHIEVEMENTS)}
        >
          <FaMedal /> Thành tích
        </button>
      </div>
      
      {/* Tab phụ cho thống kê */}
      {activeMainTab === MainTab.STATISTICS && (
        <div className="statistics-tabs secondary-tabs">
          <button 
            className={`statistics-tab ${activeStatisticsTab === StatisticsTab.TEAM ? 'active' : ''}`}
            onClick={() => handleStatisticsTabChange(StatisticsTab.TEAM)}
          >
            <FaUsers /> Thống kê đội
          </button>
          <button 
            className={`statistics-tab ${activeStatisticsTab === StatisticsTab.TOURNAMENT ? 'active' : ''}`}
            onClick={() => handleStatisticsTabChange(StatisticsTab.TOURNAMENT)}
          >
            <FaTrophy /> Thống kê giải đấu
          </button>
        </div>
      )}
      
      {/* Tab phụ cho xếp hạng */}
      {activeMainTab === MainTab.RANKING && (
        <div className="statistics-tabs secondary-tabs">
          <button 
            className={`statistics-tab ${activeRankingTab === RankingTab.MAINTENANCE_RATIO ? 'active' : ''}`}
            onClick={() => handleRankingTabChange(RankingTab.MAINTENANCE_RATIO)}
          >
            <FaBalanceScale /> Tỷ lệ duy trì
          </button>
          <button 
            className={`statistics-tab ${activeRankingTab === RankingTab.TEAM_USERS ? 'active' : ''}`}
            onClick={() => handleRankingTabChange(RankingTab.TEAM_USERS)}
          >
            <FaUsers /> Xếp hạng người dùng trong đội
          </button>
          <button 
            className={`statistics-tab ${activeRankingTab === RankingTab.TOURNAMENT_TEAMS ? 'active' : ''}`}
            onClick={() => handleRankingTabChange(RankingTab.TOURNAMENT_TEAMS)}
          >
            <FaTrophy /> Xếp hạng đội trong giải đấu
          </button>
          <button 
            className={`statistics-tab ${activeRankingTab === RankingTab.TOURNAMENT_USERS ? 'active' : ''}`}
            onClick={() => handleRankingTabChange(RankingTab.TOURNAMENT_USERS)}
          >
            <FaUser /> Xếp hạng người dùng trong giải đấu
          </button>
        </div>
      )}
      
      {/* Nội dung chính */}
      <div className="statistics-content">
        {activeMainTab === MainTab.STATISTICS && renderStatisticsContent()}
        {activeMainTab === MainTab.RANKING && renderRankingContent()}
        {activeMainTab === MainTab.ACHIEVEMENTS && renderAchievementsContent()}
      </div>
    </div>
  );
};

export default Statistics; 